import { useState, useMemo, useEffect, useCallback } from 'react'
import throttle from 'lodash.throttle'
import { useDebounceValue } from './useDebounceValue'

const SET_WIDTH_DEBOUNCE_TIMEOUT = 1000
const THROTTLE_TIMEOUT = 30

interface UseResizableSidePanel {
  defaultWidth: number
  minWidth?: number
  maxWidth?: number
  side?: 'left' | 'right'
  touchDevices?: boolean
  onWidthUpdate?: (width: number) => void
}

/**
 * Hook that returns the updated width for an specific container based on a
 * handle ref, the only limitation is that the container needs to touch left
 * or side edge of the viewport.
 *
 * It works for both pointer and touch events.
 *
 * Important note: don't try to use onDrag/onDragStart/onDragOver,
 * Firefox has an old bug and it doesn't return screen coordinates
 * while dragging: https://bugzilla.mozilla.org/show_bug.cgi?id=505521
 */
export function useResizableSidePanel({
  defaultWidth,
  minWidth = 0,
  maxWidth = document.documentElement.clientWidth,
  side = 'left',
  touchDevices = true,
  onWidthUpdate,
}: UseResizableSidePanel): {
  width: number
  isResizing: boolean
  resizeHandleRef: (node: HTMLElement | null) => void
} {
  const [width, setWidth] = useState(defaultWidth)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandleNode, setResizeHandleNode] = useState<HTMLElement | null>(
    null
  )

  // Callback ref that will be called when the element is mounted
  const resizeHandleRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setResizeHandleNode(node)
    }
  }, [])

  useEffect(() => {
    const enlargeCursor = side === 'left' ? 'e-resize' : 'w-resize'
    const shrinkCursor = side === 'left' ? 'w-resize' : 'e-resize'

    const cursor =
      width <= minWidth
        ? enlargeCursor
        : width >= maxWidth
          ? shrinkCursor
          : 'col-resize'

    // This will have the same effect as setting the cursor on hover
    // for the drag element but with the different cursor styles
    // depending on the width of the sidebar
    resizeHandleNode?.style.setProperty('cursor', cursor)

    // Set the cursor when resizing globally so that the cursor doesn't
    // flicker when the mouse moves outside the handle container
    if (isResizing) {
      document.body.style.cursor = cursor
    }

    return () => {
      document.body.style.removeProperty('cursor')
    }
  }, [resizeHandleNode, isResizing, maxWidth, minWidth, side, width])

  const handleSetWidth = useCallback((newWidth: number) => {
    setWidth(newWidth)

    // Also setting this as a CSS variable in :root so that it can be used by other components
    // that might need to know the width of the sidebar
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${newWidth}px`
    )
  }, [])

  const widthDebounced = useDebounceValue(width, SET_WIDTH_DEBOUNCE_TIMEOUT)
  useEffect(() => {
    onWidthUpdate?.(widthDebounced)
  }, [onWidthUpdate, widthDebounced])

  const dragWithMouse = useMemo(
    () =>
      throttle(({ x }: MouseEvent) => {
        const widthWithoutConstraints =
          side === 'left' ? x : document.documentElement.clientWidth - x

        // New width can have min and max constraints
        const newWidth =
          widthWithoutConstraints < minWidth
            ? minWidth
            : widthWithoutConstraints > maxWidth
              ? maxWidth
              : widthWithoutConstraints

        handleSetWidth(newWidth)
      }, THROTTLE_TIMEOUT),
    [side, minWidth, maxWidth, handleSetWidth]
  )

  const dragWithFinger = useMemo(
    () =>
      throttle((event: TouchEvent) => {
        const x = event.changedTouches[0].clientX

        const widthWithoutConstraints =
          side === 'left' ? x : document.documentElement.clientWidth - x

        // New width can have min and max constraints
        const newWidth =
          widthWithoutConstraints < minWidth
            ? minWidth
            : widthWithoutConstraints > maxWidth
              ? maxWidth
              : widthWithoutConstraints

        handleSetWidth(newWidth)
      }, THROTTLE_TIMEOUT),
    [side, minWidth, maxWidth, handleSetWidth]
  )

  useEffect(() => {
    if (!resizeHandleNode) {
      return
    }

    // ignore outside clicks while dragging
    const handleClick = (e: MouseEvent) => {
      e.stopImmediatePropagation()
    }

    const removeMouseEvents = () => {
      dragWithMouse.cancel()
      setIsResizing(false)

      document.removeEventListener('mousemove', dragWithMouse)
      document.removeEventListener('mouseup', removeMouseEvents)

      setTimeout(() =>
        document.removeEventListener('click', handleClick, { capture: true })
      )
    }

    const removeTouchEvents = () => {
      dragWithFinger.cancel()
      setIsResizing(false)

      document.removeEventListener('touchmove', dragWithFinger)
      document.removeEventListener('touchend', removeMouseEvents)
    }

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      setIsResizing(true)

      document.addEventListener('mousemove', dragWithMouse)
      document.addEventListener('click', handleClick, { capture: true })
      document.addEventListener('mouseup', removeMouseEvents)
    }

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      setIsResizing(true)

      document.addEventListener('touchmove', dragWithFinger)
      document.addEventListener('touchend', removeTouchEvents)
    }

    const resetPosition = () => {
      setWidth(defaultWidth)
      setIsResizing(false)
    }

    resizeHandleNode.addEventListener('mousedown', handleMouseDown)
    if (touchDevices) {
      resizeHandleNode.addEventListener('touchstart', handleTouchStart)
    }
    resizeHandleNode.addEventListener('dblclick', resetPosition)

    return () => {
      resizeHandleNode.removeEventListener('mousedown', handleMouseDown)
      resizeHandleNode.removeEventListener('dblclick', resetPosition)

      if (touchDevices) {
        resizeHandleNode.removeEventListener('touchstart', handleTouchStart)
      }

      removeMouseEvents()
      removeTouchEvents()
    }
  }, [
    resizeHandleNode,
    dragWithFinger,
    dragWithMouse,
    defaultWidth,
    touchDevices,
  ])

  return { width, isResizing, resizeHandleRef }
}
